import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { Download, FileText, Filter, CheckSquare, Square, Package2 } from "lucide-react";
import { toast } from "sonner";
import { LedgerViewerDialog } from "@/components/LedgerButtons";

const TYPE_LABEL = {
  PAYMENT_BREAKDOWN: "Desglose de pago",
  SETTLEMENT: "Liquidación",
  AUCTION_FEE: "Cobro de días de subasta",
};
const SETT_LABEL = {
  complete: "Completa",
  partial: "Parcial",
  full_refund: "Devolución total",
  cancelled_no_charge: "Cancelado sin cobro",
};
const STATE_CLASS = {
  RETAINED: "bg-amber-500/20 text-amber-300",
  SETTLED_COMPLETE: "bg-emerald-500/20 text-emerald-300",
  SETTLED_PARTIAL: "bg-blue-500/20 text-blue-300",
  REFUNDED_FULL: "bg-red-500/20 text-red-300",
  CANCELLED_NO_CHARGE: "bg-neutral-500/20 text-neutral-300",
  HITTCG_INCOME: "bg-[#EF4444]/20 text-red-300",
};

export default function AdminContabilidad() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", doc_type: "", settlement_type: "", source: "",
  });

  const load = async () => {
    setLoading(true);
    const params = {};
    for (const k of Object.keys(filters)) if (filters[k]) params[k] = filters[k];
    const r = await api.get("/admin/ledger", { params });
    setDocs(r.data);
    setSelected(new Set());
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const toggleAll = () => {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  };

  const exportBulk = async (format) => {
    if (docs.length === 0) { toast.error("Nada que exportar"); return; }
    const body = selected.size > 0
      ? { doc_ids: [...selected], format }
      : { ...filters, format };
    try {
      const r = await api.post("/admin/ledger/export", body, { responseType: "blob" });
      const mime = format === "csv" ? "text/csv" : "application/zip";
      const ext = format === "csv" ? "csv" : "zip";
      const url = window.URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hittcg-ledger-${new Date().toISOString().slice(0, 10)}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado ${selected.size || docs.length} documento(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al exportar");
    }
  };

  const downloadOne = async (doc) => {
    try {
      const r = await api.get(`/ledger/${doc.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${doc.doc_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Error al descargar"); }
  };

  const totalsBySelection = () => {
    const arr = selected.size > 0 ? docs.filter(d => selected.has(d.id)) : docs;
    let gross = 0, sellerPaid = 0, refunded = 0, hitkept = 0, ppkept = 0;
    for (const d of arr) {
      const a = d.amounts || {};
      gross += Number(a.gross_paid_by_buyer || a.gross_original || a.gross_paid_by_seller || 0);
      sellerPaid += Number(a.seller_paid || 0);
      refunded += Number(a.buyer_refunded || 0);
      hitkept += Number(a.hittcg_kept || 0);
      ppkept += Number(a.payphone_kept || 0);
    }
    return { gross, sellerPaid, refunded, hitkept, ppkept, count: arr.length };
  };
  const tot = totalsBySelection();

  return (
    <div data-testid="admin-contabilidad">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><FileText size={22} className="text-[#EF4444]" />Contabilidad — Ledger inmutable</h2>
          <p className="text-xs text-neutral-500 mt-1">Todos los documentos generados: desgloses de pago (DOC 1), liquidaciones (DOC 2) y cobros de días de subasta (SU-DIA). Ventas y subastas en el mismo libro. Inmutables. Con descarga individual y masiva.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="ledger-export-zip" onClick={() => exportBulk("zip")} className="bg-[#EF4444] hover:bg-[#DC2626]">
            <Download size={14} className="mr-1" />Descargar ZIP {selected.size > 0 ? `(${selected.size})` : "(filtrado)"}
          </Button>
          <Button data-testid="ledger-export-csv" onClick={() => exportBulk("csv")} variant="outline" className="border-[#EF4444]/50 text-[#EF4444]">
            <Download size={14} className="mr-1" />CSV consolidado
          </Button>
        </div>
      </div>

      <div className="bg-[#111] border border-[#262626] rounded-md p-3 mb-4 flex flex-wrap gap-3 items-end" data-testid="ledger-filters">
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase">Desde</Label>
          <Input data-testid="ledger-filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} className="bg-[#171717] border-[#262626] w-40" />
        </div>
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase">Hasta</Label>
          <Input data-testid="ledger-filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} className="bg-[#171717] border-[#262626] w-40" />
        </div>
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase">Tipo doc</Label>
          <Select value={filters.doc_type || "all"} onValueChange={(v) => setFilters(f => ({ ...f, doc_type: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="ledger-filter-type" className="bg-[#171717] border-[#262626] w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="PAYMENT_BREAKDOWN">Desglose de pago</SelectItem>
              <SelectItem value="SETTLEMENT">Liquidación</SelectItem>
              <SelectItem value="AUCTION_FEE">Cobro de días de subasta</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase">Origen</Label>
          <Select value={filters.source || "all"} onValueChange={(v) => setFilters(f => ({ ...f, source: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="ledger-filter-source" className="bg-[#171717] border-[#262626] w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="sale">Ventas</SelectItem>
              <SelectItem value="auction">Subastas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-neutral-500 uppercase">Desenlace</Label>
          <Select value={filters.settlement_type || "all"} onValueChange={(v) => setFilters(f => ({ ...f, settlement_type: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="ledger-filter-settlement" className="bg-[#171717] border-[#262626] w-52"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626] text-white">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="complete">Completa</SelectItem>
              <SelectItem value="partial">Parcial</SelectItem>
              <SelectItem value="full_refund">Devolución total</SelectItem>
              <SelectItem value="cancelled_no_charge">Cancelado sin cobro</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="ledger-apply-filters" onClick={load} variant="outline" className="border-[#EF4444]/50 text-[#EF4444]">
          <Filter size={14} className="mr-1" />Aplicar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Stat label={`${selected.size > 0 ? "Seleccionado" : "Filtrado"} (${tot.count})`} value={`$${tot.gross.toFixed(2)}`} accent="text-white" />
        <Stat label="Pagado a vendedores" value={`$${tot.sellerPaid.toFixed(2)}`} accent="text-emerald-400" />
        <Stat label="Devuelto a compradores" value={`$${tot.refunded.toFixed(2)}`} accent="text-red-400" />
        <Stat label="Retenido HitTCG" value={`$${tot.hitkept.toFixed(2)}`} accent="text-[#EF4444]" />
        <Stat label="Retenido Payphone" value={`$${tot.ppkept.toFixed(2)}`} accent="text-blue-300" />
      </div>

      <div className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
        <div className="p-3 border-b border-[#262626] flex items-center gap-2">
          <button data-testid="ledger-select-all" type="button" onClick={toggleAll} className="text-xs text-neutral-400 hover:text-white flex items-center gap-1">
            {selected.size === docs.length && docs.length > 0 ? <CheckSquare size={14} /> : <Square size={14} />}
            {selected.size === docs.length && docs.length > 0 ? "Deseleccionar todo lo filtrado" : "Seleccionar todo lo filtrado"}
          </button>
          <span className="ml-auto text-xs text-neutral-500">{selected.size} de {docs.length}</span>
        </div>
        {loading ? <div className="p-6 text-neutral-500">Cargando…</div> : docs.length === 0 ? (
          <div className="p-8 text-center text-neutral-500">Sin documentos que coincidan.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="p-3 w-8"></th>
                  <th className="text-left p-3">N° / Fecha</th>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Estado dinero</th>
                  <th className="text-left p-3">Pedido / Partes</th>
                  <th className="text-right p-3">Bruto</th>
                  <th className="text-right p-3">Vendedor</th>
                  <th className="text-right p-3">Reembolso</th>
                  <th className="text-right p-3">HitTCG</th>
                  <th className="text-right p-3">Payphone</th>
                  <th className="text-center p-3">Cuadre</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => {
                  const a = d.amounts || {};
                  const isPB = d.type === "PAYMENT_BREAKDOWN";
                  const isFee = d.type === "AUCTION_FEE";
                  const gross = a.gross_paid_by_buyer || a.gross_original || a.gross_paid_by_seller || 0;
                  return (
                    <tr key={d.id} data-testid={`ledger-row-${d.id}`} className="border-t border-[#171717] hover:bg-[#0F0F0F]">
                      <td className="p-3">
                        <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`ledger-check-${d.id}`} className="border-[#404040] data-[state=checked]:bg-[#EF4444]" />
                      </td>
                      <td className="p-3">
                        <div className="font-mono text-xs text-neutral-300">{d.doc_number}</div>
                        <div className="text-[10px] text-neutral-500">{new Date(d.created_at).toLocaleDateString("es-EC")}</div>
                      </td>
                      <td className="p-3 text-sm">
                        {d.source === "auction" && <span className="inline-block mr-1.5 mb-0.5 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-[#EF4444]/15 border border-[#EF4444]/40 text-red-300" data-testid={`ledger-auction-${d.id}`}>SUBASTA</span>}
                        {TYPE_LABEL[d.type]}
                        {d.settlement_type && <div className="text-[10px] text-neutral-500">{SETT_LABEL[d.settlement_type]}</div>}
                      </td>
                      <td className="p-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded ${STATE_CLASS[d.money_state] || ""}`}>{d.money_state}</span></td>
                      <td className="p-3 text-xs">
                        {d.order_id
                          ? <Link to={`/order/${d.order_id}`} className="font-mono text-[#EF4444] hover:underline">#{d.order_id.slice(0, 8)}</Link>
                          : <Link to={`/subastas/${d.auction_id}`} className="font-mono text-[#EF4444] hover:underline">Subasta #{(d.auction_id || "").slice(0, 8)}</Link>}
                        {d.auction?.product_name && <div className="text-neutral-400 truncate max-w-[220px]">{d.auction.product_name}</div>}
                        <div className="text-neutral-500 truncate max-w-[220px]">{isFee ? `Pagó: ${d.seller?.name || "—"} (vendedor)` : `${d.buyer?.name} → ${d.seller?.name}`}</div>
                      </td>
                      <td className="p-3 text-right text-neutral-200">${Number(gross).toFixed(2)}</td>
                      <td className="p-3 text-right text-emerald-400">{isPB || isFee ? "—" : `$${Number(a.seller_paid || 0).toFixed(2)}`}</td>
                      <td className="p-3 text-right text-red-400">{isPB || isFee ? "—" : `$${Number(a.buyer_refunded || 0).toFixed(2)}`}</td>
                      <td className="p-3 text-right text-[#EF4444]">${Number(isPB ? a.hittcg_commission : a.hittcg_kept || 0).toFixed(2)}</td>
                      <td className="p-3 text-right text-blue-300">${Number(isPB ? a.payphone_fee : a.payphone_kept || 0).toFixed(2)}</td>
                      <td className="p-3 text-center">
                        {isPB ? <span className="text-neutral-500">—</span> :
                         a.balances ? <span className="text-emerald-400">✓</span> : <span className="text-red-400">✗</span>}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" data-testid={`ledger-view-${d.id}`} onClick={() => setViewing(d)} className="text-neutral-400 hover:text-white h-7">
                          Abrir
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`ledger-dl-${d.id}`} onClick={() => downloadOne(d)} className="text-[#EF4444] h-7">
                          <Download size={12} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <LedgerViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-[#111] border border-[#262626] rounded-md p-3">
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
      <div className={`font-display text-xl mt-1 ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}
